import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link as RouterLink } from 'react-router-dom';
import { updateForumVisit } from '../utils/notificationManager';
import { getForumPosts, createForumPost, updatePostLikes, subscribeToForumPosts, deleteForumPost, getPostComments, createComment, deleteComment } from '../utils/forumService';
import { uploadMultipleMedia } from '../utils/mediaUpload';
import { supabase } from '../utils/supabaseClient';
import { getSavedForumPosts, saveForumPost, unsaveForumPost } from '../utils/syncService';
import { notifyMembersOfNewPost, notifyPostLike, notifyPostComment } from '../utils/notificationService';

const CommunityForum = () => {
  // Track forum visit for notifications
  useEffect(() => {
    updateForumVisit();
  }, []);
  const [showNewPostModal, setShowNewPostModal] = useState(false);
  const [showNewPostForm, setShowNewPostForm] = useState(false);
  const [newPostTitle, setNewPostTitle] = useState('');
  const [newPostContent, setNewPostContent] = useState('');
  const [newPostCategory, setNewPostCategory] = useState('general');
  const [selectedMedia, setSelectedMedia] = useState([]);
  const [mediaPreviews, setMediaPreviews] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);

  // Check for shared workout stats
  useEffect(() => {
    try {
      const search = window.location.search || (window.location.hash.includes('?') ? '?' + window.location.hash.split('?')[1] : '');
      const params = new URLSearchParams(search);
      
      // Check if sharing a workout
      if (params.get('share') === 'workout') {
        const shareData = localStorage.getItem('shareWorkoutStats');
        if (shareData) {
          const { text, timestamp } = JSON.parse(shareData);
          // Only use if less than 5 minutes old
          if (Date.now() - timestamp < 300000) {
            setNewPostContent(text);
            setNewPostCategory('general');
            setShowNewPostForm(true);
            // Clear the stored data
            localStorage.removeItem('shareWorkoutStats');
          }
        }
      }
    } catch (e) {
      console.log('Error loading shared workout:', e);
    }
  }, []);

  // Read tab from URL (?tab=...) and map to a category
  useEffect(() => {
    try {
      const search = window.location.search || (window.location.hash.includes('?') ? '?' + window.location.hash.split('?')[1] : '');
      const params = new URLSearchParams(search);
      const tab = (params.get('tab') || '').toLowerCase();
      let category = 'all';
      switch (tab) {
        case 'ask-kate': // legacy links
          category = 'womens-health';
          break;
        case 'womens-health':
          category = 'womens-health';
          break;
        case 'eczema-hacks':
          category = 'eczema-hacks';
          break;
        case 'success-stories':
          category = 'fitness-wins';
          break;
        case 'qna':
          category = 'all';
          break;
        case 'challenges':
          category = 'fitness-wins';
          break;
        default:
          break;
      }
      setActiveCategory(category);
    } catch {}
  }, []);

  

  const [activeCategory, setActiveCategory] = useState('all');
  const [likedPosts, setLikedPosts] = useState(new Set());
  const [savedPosts, setSavedPosts] = useState(new Set());
  
  // Comment state
  const [expandedComments, setExpandedComments] = useState(new Set());
  const [postComments, setPostComments] = useState({});
  const [commentInputs, setCommentInputs] = useState({});
  const [loadingComments, setLoadingComments] = useState({});

  const categories = [
    { id: 'all', name: 'All Posts', icon: '💬', color: 'bg-purple-500' },
    { id: 'food', name: 'Food', icon: '🍽️', color: 'bg-orange-500' },
    { id: 'mindset', name: 'Mindset', icon: '🧠', color: 'bg-teal-500' },
    { id: 'womens-health', name: "Women's Health", icon: '🌸', color: 'bg-pink-500' },
    { id: 'general', name: 'General', icon: '💭', color: 'bg-blue-500' },
    { id: 'eczema', name: 'Eczema', icon: '✨', color: 'bg-green-500' },
    { id: 'other', name: 'Other', icon: '📌', color: 'bg-gray-500' }
  ];

  const [posts, setPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(0);
  const POSTS_PER_PAGE = 20;
  const [currentUserId, setCurrentUserId] = useState(null);

  // Get current user and check if admin
  useEffect(() => {
    const getCurrentUser = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      setCurrentUserId(user?.id);
      
      // Check if user is admin from members table
      if (user?.id) {
        const { data: member } = await supabase
          .from('members')
          .select('is_admin, email')
          .eq('auth_id', user.id)
          .single();
        
        if (member?.is_admin || member?.email === 'hello@alera.co.nz') {
          setIsAdmin(true);
          console.log('✅ Admin access enabled for forum posts');
        }
      }
    };
    getCurrentUser();
  }, []);

  // Load saved posts from Supabase
  useEffect(() => {
    const loadSavedPosts = async () => {
      const userEmail = localStorage.getItem('userEmail');
      if (!userEmail) return;
      
      const result = await getSavedForumPosts(userEmail);
      if (result.success) {
        setSavedPosts(new Set(result.data));
      }
    };
    loadSavedPosts();
  }, []);

  // Load posts from Supabase on mount - highly optimized with pagination
  useEffect(() => {
    let mounted = true;
    let subscriptionRef = null;

    const loadPosts = async () => {
      const result = await getForumPosts({ 
        limit: POSTS_PER_PAGE, 
        offset: 0 
      });
      
      if (mounted && result.success) {
        setPosts(result.data);
        setHasMore(result.hasMore || false);
        setPage(1);
        
        // If data came from cache, don't show loading state
        if (result.fromCache) {
          setLoadingPosts(false);
        } else {
          // Small delay to show skeleton briefly for UX consistency
          setTimeout(() => {
            if (mounted) setLoadingPosts(false);
          }, 100);
        }
      } else if (mounted) {
        console.error('Failed to load posts:', result.error);
        setLoadingPosts(false);
      }
    };

    // Start loading immediately
    loadPosts();

    // Delay real-time subscription to not block initial load
    setTimeout(() => {
      if (!mounted) return;
      
      subscriptionRef = subscribeToForumPosts((payload) => {
        if (!mounted) return;
        
        if (payload.eventType === 'INSERT') {
          setPosts(prev => [payload.new, ...prev]);
        } else if (payload.eventType === 'UPDATE') {
          setPosts(prev => prev.map(post => 
            post.id === payload.new.id ? payload.new : post
          ));
        } else if (payload.eventType === 'DELETE') {
          setPosts(prev => prev.filter(post => post.id !== payload.old.id));
        }
      });
    }, 500); // Delay subscription by 500ms

    return () => {
      mounted = false;
      if (subscriptionRef) {
        subscriptionRef.unsubscribe();
      }
    };
  }, []);

  // Load more posts (infinite scroll)
  const loadMorePosts = async () => {
    if (loadingMore || !hasMore) return;
    
    setLoadingMore(true);
    const result = await getForumPosts({ 
      limit: POSTS_PER_PAGE, 
      offset: page * POSTS_PER_PAGE 
    });
    
    if (result.success) {
      setPosts(prev => [...prev, ...result.data]);
      setHasMore(result.hasMore || false);
      setPage(prev => prev + 1);
    }
    setLoadingMore(false);
  };

  // Filter posts by category
  const filteredPosts = (activeCategory === 'all' 
    ? posts 
    : posts.filter(post => post.category === activeCategory));

  const handleLike = async (postId) => {
    const post = posts.find(p => p.id === postId);
    if (!post) return;

    const newLikedPosts = new Set(likedPosts);
    let newLikesCount;
    const isLiking = !likedPosts.has(postId);

    if (likedPosts.has(postId)) {
      newLikedPosts.delete(postId);
      newLikesCount = post.likes - 1;
    } else {
      newLikedPosts.add(postId);
      newLikesCount = post.likes + 1;
    }

    // Update UI immediately
    setPosts(posts.map(p => 
      p.id === postId ? { ...p, likes: newLikesCount } : p
    ));
    setLikedPosts(newLikedPosts);

    // Update in Supabase
    const result = await updatePostLikes(postId, newLikesCount);
    
    if (!result.success) {
      // Revert on error
      setPosts(posts.map(p => 
        p.id === postId ? { ...p, likes: post.likes } : p
      ));
      setLikedPosts(likedPosts);
    } else if (isLiking) {
      // Send notification to post author when someone likes their post
      const likerName = localStorage.getItem('userName') || 'A member';
      const postContent = post.content || post.title || 'your post';
      const authorEmail = post.user_email;
      console.log('🔔 Like notification — post author email:', authorEmail, '| post id:', postId);
      if (authorEmail) {
        notifyPostLike(authorEmail, likerName, postId, postContent).then(res => 
          console.log('🔔 Like notification result:', res)
        ).catch(err => 
          console.warn('❌ Failed to send like notification:', err)
        );
      } else {
        console.warn('⚠️ Cannot notify — post has no user_email stored');
      }
    }
  };

  const handleSavePost = async (postId) => {
    const userEmail = localStorage.getItem('userEmail');
    const newSavedPosts = new Set(savedPosts);
    
    if (savedPosts.has(postId)) {
      newSavedPosts.delete(postId);
      if (userEmail) await unsaveForumPost(userEmail, postId);
    } else {
      newSavedPosts.add(postId);
      if (userEmail) await saveForumPost(userEmail, postId);
    }
    
    setSavedPosts(newSavedPosts);
  };

  const handleMediaUpload = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;

    console.log('📷 Media selected:', files.length, 'files');
    
    // Add to existing media
    const newMedia = [...selectedMedia, ...files];
    setSelectedMedia(newMedia);
    
    // Create previews for each file
    files.forEach(file => {
      const isVideo = file.type.startsWith('video/');
      
      if (isVideo) {
        // For videos, create object URL for preview
        const videoUrl = URL.createObjectURL(file);
        setMediaPreviews(prev => [...prev, { type: 'video', url: videoUrl, file }]);
      } else {
        // For images, create data URL preview
        const reader = new FileReader();
        reader.onload = (e) => {
          setMediaPreviews(prev => [...prev, { type: 'image', url: e.target.result, file }]);
        };
        reader.onerror = (error) => {
          console.error('❌ FileReader error:', error);
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleRemoveMedia = (index) => {
    setSelectedMedia(prev => prev.filter((_, i) => i !== index));
    setMediaPreviews(prev => {
      // Clean up object URLs for videos
      if (prev[index]?.type === 'video') {
        URL.revokeObjectURL(prev[index].url);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDeletePost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    const result = await deleteForumPost(postId);
    if (result.success) {
      // Remove from UI immediately
      setPosts(prev => prev.filter(post => post.id !== postId));
    } else {
      alert('Failed to delete post: ' + result.error);
    }
  };

  // Comment handlers
  const toggleComments = async (postId) => {
    const newExpanded = new Set(expandedComments);
    
    if (newExpanded.has(postId)) {
      newExpanded.delete(postId);
    } else {
      newExpanded.add(postId);
      // Load comments if not already loaded
      if (!postComments[postId]) {
        setLoadingComments(prev => ({ ...prev, [postId]: true }));
        const result = await getPostComments(postId);
        if (result.success) {
          setPostComments(prev => ({ ...prev, [postId]: result.data }));
        }
        setLoadingComments(prev => ({ ...prev, [postId]: false }));
      }
    }
    
    setExpandedComments(newExpanded);
  };

  const handleAddComment = async (postId) => {
    const content = commentInputs[postId];
    if (!content || !content.trim()) return;

    const result = await createComment(postId, content);
    if (result.success) {
      // Add comment to UI
      setPostComments(prev => ({
        ...prev,
        [postId]: [...(prev[postId] || []), result.data]
      }));
      // Clear input
      setCommentInputs(prev => ({ ...prev, [postId]: '' }));
      // Update post reply count in UI
      setPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, replies: post.replies + 1 } : post
      ));
      
      // Send notification to post author
      const post = posts.find(p => p.id === postId);
      const authorEmail = post?.user_email;
      console.log('🔔 Comment notification — post author email:', authorEmail, '| post id:', postId);
      if (post && authorEmail) {
        const commenterName = localStorage.getItem('userName') || 'A member';
        const postContent = post.content || post.title || 'your post';
        notifyPostComment(authorEmail, commenterName, postId, postContent, content).then(res =>
          console.log('🔔 Comment notification result:', res)
        ).catch(err => 
          console.warn('❌ Failed to send comment notification:', err)
        );
      } else {
        console.warn('⚠️ Cannot notify commenter — post has no user_email stored');
      }
    } else {
      alert('Failed to add comment: ' + result.error);
    }
  };

  const handleDeleteComment = async (postId, commentId) => {
    if (!window.confirm('Delete this comment?')) return;

    const result = await deleteComment(commentId);
    if (result.success) {
      // Remove from UI
      setPostComments(prev => ({
        ...prev,
        [postId]: prev[postId].filter(c => c.id !== commentId)
      }));
      // Update post reply count in UI
      setPosts(prev => prev.map(post => 
        post.id === postId ? { ...post, replies: Math.max(0, post.replies - 1) } : post
      ));
    } else {
      alert('Failed to delete comment: ' + result.error);
    }
  };

  const handleSharePost = async (post) => {
    try {
      // Get image URL from post
      let imageUrl = null;
      try {
        const mediaUrls = JSON.parse(post.image_url);
        if (Array.isArray(mediaUrls) && mediaUrls.length > 0) {
          imageUrl = mediaUrls[0]; // Share first image
        }
      } catch {
        if (post.image_url && (post.image_url.startsWith('http') || post.image_url.startsWith('data:'))) {
          imageUrl = post.image_url;
        }
      }

      // Prepare caption text
      const caption = post.content.substring(0, 200) + (post.content.length > 200 ? '...' : '');

      // If there's an image, fetch it and share with caption
      if (imageUrl && navigator.canShare && imageUrl.startsWith('http')) {
        try {
          const response = await fetch(imageUrl);
          const blob = await response.blob();
          const file = new File([blob], 'alera-post.jpg', { type: blob.type });
          
          const shareDataWithImage = {
            files: [file],
            title: `${post.author} shared on Alera Community`,
            text: `${caption}\n\n#AleraCommunity`
          };
          
          // Check if sharing files with text is supported
          if (navigator.canShare(shareDataWithImage)) {
            await navigator.share(shareDataWithImage);
            return;
          }
        } catch (err) {
          console.log('Image sharing not supported, sharing text only');
        }
      }

      // Fallback to text + URL if image sharing failed or no image
      const shareDataText = {
        title: `${post.author} shared on Alera Community`,
        text: caption,
        url: `https://alera.co.nz/community-forum?post=${post.id}`
      };
      
      if (navigator.share) {
        await navigator.share(shareDataText);
      } else {
        // Desktop fallback: copy to clipboard
        await navigator.clipboard.writeText(`${caption}\n\n${shareDataText.url}`);
        alert('Post copied to clipboard!');
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Error sharing:', error);
      }
    }
  };

  const handleSubmitPost = async () => {
    if (newPostContent.trim() && !isSubmitting) {
      setIsSubmitting(true);
      try {
        let mediaUrls = [];

        // Upload multiple media files to Supabase Storage if present
        if (selectedMedia.length > 0) {
          console.log('Uploading', selectedMedia.length, 'media files...');
          const uploadResult = await uploadMultipleMedia(selectedMedia, 'user-' + Date.now());
          console.log('Upload result:', uploadResult);
          
          if (uploadResult.success && uploadResult.urls) {
            mediaUrls = uploadResult.urls;
            console.log('Media URLs to save:', mediaUrls);
            
            if (uploadResult.errors && uploadResult.errors.length > 0) {
              console.warn('Some uploads failed:', uploadResult.errors);
            }
          } else {
            console.error('Media upload failed:', uploadResult.errors);
            alert('Failed to upload media: ' + (uploadResult.errors?.join(', ') || 'Unknown error'));
            return;
          }
        }

        // Get user name and profile picture
        const userName = localStorage.getItem('memberProfileName') || localStorage.getItem('userName') || localStorage.getItem('userEmail')?.split('@')[0] || 'Member';
        const memberAvatarUrl = localStorage.getItem('memberAvatarUrl') || null;
        
        const postData = {
          content: newPostContent,
          category: newPostCategory,
          author: userName,
          avatar: memberAvatarUrl || '👤',
          image: mediaUrls.length > 0 ? JSON.stringify(mediaUrls) : null // Store as JSON array
        };
        
        console.log('Post data being sent:', postData);

        // Create post in Supabase
        const result = await createForumPost(postData);
        
        if (result.success) {
          // Send notifications to all members about the new post
          try {
            const authorEmail = localStorage.getItem('userEmail');
            const preview = newPostContent.substring(0, 150) + (newPostContent.length > 150 ? '...' : '');
            
            // Create in-app + browser push notifications for all members
            await notifyMembersOfNewPost({
              postId: result.data?.id,
              authorEmail: authorEmail,
              authorName: userName,
              category: newPostCategory,
              preview: preview,
              isAdmin: isAdmin
            });
          } catch (e) {
            console.warn('Failed to send notifications:', e);
          }
          
          // Real-time subscription will add it to the list
          setNewPostContent('');
          setNewPostCategory('general');
          setSelectedMedia([]);
          setMediaPreviews([]);
          setShowNewPostForm(false);
        } else {
          console.error('Failed to create post:', result.error);
          alert('Failed to create post. Please try again.');
        }
      } catch (error) {
        console.error('Error submitting post:', error);
        alert('An error occurred. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const getCategoryInfo = (categoryId) => {
    return categories.find(cat => cat.id === categoryId) || categories[0];
  };

  const getTimeAgo = (timestamp) => {
    if (!timestamp) return 'just now';
    const now = new Date();
    const postDate = new Date(timestamp);
    const seconds = Math.floor((now - postDate) / 1000);
    
    if (seconds < 60) return 'just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
    return `${Math.floor(seconds / 604800)}w ago`;
  };
  const totalPosts = posts.length;
  const todaysPosts = posts.filter(post => {
    if (!post.created_at) return false;
    const postDate = new Date(post.created_at);
    const now = new Date();
    const hoursDiff = (now - postDate) / (1000 * 60 * 60);
    return hoursDiff < 24;
  }).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-pink-50 pt-20 px-3 sm:px-6 pb-8">
      <div className="max-w-2xl mx-auto">
        {/* Header - Instagram Style */}
        <div className="bg-white border-b border-gray-200 sticky top-0 z-10 px-3 md:px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 md:space-x-3">
              <RouterLink
                to="/dashboard"
                className="text-gray-600 hover:text-gray-800 transition-colors"
              >
                <svg className="w-5 h-5 md:w-6 md:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </RouterLink>
              <h1 className="text-lg md:text-xl font-semibold text-gray-800">Community</h1>
            </div>
            <div className="flex items-center space-x-2 md:space-x-4">
              {isAdmin && (
                <span className="hidden sm:inline-block px-3 py-1 bg-sage-100 text-sage-700 rounded-full text-xs font-medium">Kate (Admin)</span>
              )}
              <button
                onClick={() => setShowNewPostForm(true)}
                className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-3 md:px-4 py-1.5 md:py-2 rounded-full text-xs md:text-sm font-medium hover:shadow-lg transition-all duration-200"
              >
                + Share
              </button>
            </div>
          </div>
        </div>

        {/* Inspirational Welcome Section */}
        <div className="bg-gradient-to-r from-purple-50 to-pink-50 border-b border-gray-100">
          <div className="px-4 py-3 md:py-6">
            <div className="text-center mb-2 md:mb-4">
              <h2 className="text-lg md:text-2xl font-bold text-gray-800 mb-2 md:mb-3">Your Safe Space to Heal & Thrive ✨</h2>
              <p className="text-xs md:text-base text-gray-600 leading-relaxed max-w-2xl mx-auto hidden md:block">
                This is more than just a forum—it's your wellness family. Here, every story matters, every win deserves celebration, and every struggle finds understanding. 
              </p>
              <p className="text-xs text-gray-600 md:hidden">
                Share your journey, celebrate wins, find strength together 💜
              </p>
            </div>
            
            <div className="hidden md:grid md:grid-cols-3 gap-4 max-w-4xl mx-auto">
              <div className="bg-white/70 rounded-lg p-4 text-center">
                <div className="text-2xl mb-2">🤝</div>
                <h3 className="font-semibold text-gray-800 mb-1">Share Your Journey</h3>
                <p className="text-sm text-gray-600">Your experiences help others feel less alone and discover new solutions</p>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 text-center">
                <div className="text-2xl mb-2">🌟</div>
                <h3 className="font-semibold text-gray-800 mb-1">Celebrate Every Win</h3>
                <p className="text-sm text-gray-600">From small daily victories to major breakthroughs—we're here to cheer you on</p>
              </div>
              
              <div className="bg-white/70 rounded-lg p-4 text-center">
                <div className="text-2xl mb-2">💪</div>
                <h3 className="font-semibold text-gray-800 mb-1">Find Your Strength</h3>
                <p className="text-sm text-gray-600">Connect with others who truly understand and gain power from shared wisdom</p>
              </div>
            </div>
            
            <div className="text-center mt-3 md:mt-4 hidden md:block">
              <p className="text-sm text-gray-500 italic">
                "When we share our stories, we realize we're not alone—and that's where healing begins." 💜
              </p>
            </div>
          </div>
        </div>


        {/* Stories-style Category Filters */}
        <div className="px-4 py-3 border-b border-gray-200 bg-white">
          <div className="flex space-x-4 overflow-x-auto scrollbar-hide">
            <button
              onClick={() => setActiveCategory('all')}
              className={`flex-shrink-0 flex flex-col items-center space-y-1 ${
                activeCategory === 'all' ? 'opacity-100' : 'opacity-60'
              }`}
            >
              <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl ${
                activeCategory === 'all' 
                  ? 'bg-gradient-to-r from-purple-500 to-pink-500 ring-2 ring-purple-400 ring-offset-2' 
                  : 'bg-gray-100'
              }`}>
                🌟
              </div>
              <span className="text-xs font-medium text-gray-600">All</span>
            </button>
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`flex-shrink-0 flex flex-col items-center space-y-1 ${
                  activeCategory === category.id ? 'opacity-100' : 'opacity-60'
                }`}
              >
                <div className={`w-16 h-16 rounded-full flex items-center justify-center text-2xl ${
                  activeCategory === category.id 
                    ? `${category.color} ring-2 ring-purple-400 ring-offset-2` 
                    : 'bg-gray-100'
                }`}>
                  {category.icon}
                </div>
                <span className="text-xs font-medium text-gray-600">{category.name}</span>
              </button>
            ))}
          </div>
        </div>

        {/* New Post Form Modal - Instagram Style */}
        {showNewPostForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <button
                  onClick={() => setShowNewPostForm(false)}
                  className="text-gray-600 hover:text-gray-800"
                >
                  Cancel
                </button>
                <h2 className="text-lg font-semibold text-gray-800">New Post</h2>
                <button
                  onClick={handleSubmitPost}
                  className="text-blue-500 font-semibold hover:text-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={!newPostContent.trim() || isSubmitting}
                >
                  {isSubmitting ? 'Posting...' : 'Share'}
                </button>
              </div>

              {/* User Info */}
              <div className="flex items-center space-x-3 p-4 border-b border-gray-100">
                <div className="w-10 h-10 rounded-full bg-gradient-to-r from-green-400 to-blue-400 flex items-center justify-center text-lg">
                  👤
                </div>
                <div>
                  <span className="font-semibold text-gray-800 text-sm">You</span>
                  <div className="flex items-center space-x-2 mt-1">
                    <select
                      value={newPostCategory}
                      onChange={(e) => setNewPostCategory(e.target.value)}
                      className="text-xs bg-gray-100 border-none rounded-full px-2 py-1 focus:ring-2 focus:ring-blue-500"
                    >
                      {categories.slice(1).map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.icon} {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Content Input */}
              <div className="p-4">
                <textarea
                  value={newPostContent}
                  onChange={(e) => setNewPostContent(e.target.value)}
                  placeholder="What's on your mind? Share your wellness journey..."
                  rows="6"
                  className="w-full text-gray-800 placeholder-gray-400 border-none outline-none resize-none text-sm"
                />
              </div>

              {/* Media Previews */}
              {mediaPreviews.length > 0 && (
                <div className="px-4 pb-4">
                  <div className="grid grid-cols-2 gap-2">
                    {mediaPreviews.map((preview, index) => (
                      <div key={index} className="relative">
                        {preview.type === 'video' ? (
                          <video
                            src={preview.url}
                            className="w-full h-40 object-cover rounded-lg"
                            controls
                          />
                        ) : (
                          <img
                            src={preview.url}
                            alt={`Preview ${index + 1}`}
                            className="w-full h-40 object-cover rounded-lg"
                          />
                        )}
                        <button
                          onClick={() => handleRemoveMedia(index)}
                          className="absolute top-2 right-2 bg-black bg-opacity-50 text-white rounded-full w-7 h-7 flex items-center justify-center hover:bg-opacity-70"
                        >
                          ✕
                        </button>
                        <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                          {preview.type === 'video' ? '🎥' : '📷'}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Photo Upload Options */}
              <div className="px-4 pb-4 border-t border-gray-100 pt-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Add to your post</span>
                  <div className="flex items-center space-x-3">
                    {/* Gallery Upload - Images & Videos */}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*,video/*"
                        multiple
                        onChange={handleMediaUpload}
                        className="hidden"
                      />
                      <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center hover:bg-green-200 transition-colors">
                        <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    </label>

                    {/* Camera Capture */}
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*,video/*"
                        capture="environment"
                        onChange={handleMediaUpload}
                        className="hidden"
                      />
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center hover:bg-blue-200 transition-colors">
                        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                        </svg>
                      </div>
                    </label>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Instagram-style Posts Feed */}
        <div className="space-y-0">
          {loadingPosts ? (
            // Skeleton loader for better perceived performance
            <div className="bg-white">
              {[1, 2].map((i) => (
                <div key={i} className="border-b border-gray-200 p-4 animate-pulse">
                  <div className="flex items-center space-x-3 mb-3">
                    <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
                      <div className="h-3 bg-gray-200 rounded w-16"></div>
                    </div>
                  </div>
                  <div className="space-y-2">
                    <div className="h-4 bg-gray-200 rounded w-full"></div>
                    <div className="h-4 bg-gray-200 rounded w-3/4"></div>
                  </div>
                </div>
              ))}
            </div>
          ) : filteredPosts.length === 0 ? (
            <div className="text-center py-12 bg-white">
              <div className="text-6xl mb-4">🌱</div>
              <h3 className="text-xl font-semibold text-gray-600 mb-2">No posts yet in this category</h3>
              <p className="text-gray-500">Be the first to share something!</p>
            </div>
          ) : (
            filteredPosts.map((post) => {
              const category = categories.find(cat => cat.id === post.category);
              return (
                <motion.div
                  key={post.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="bg-white border-b border-gray-200"
                >
                  {/* Post Header */}
                  <div className="flex items-center justify-between p-4">
                    <div className="flex items-center space-x-3">
                      {post.avatar && post.avatar.startsWith('http') ? (
                        <img
                          src={post.avatar}
                          alt={post.author}
                          className="w-10 h-10 rounded-full object-cover ring-2 ring-purple-200"
                        />
                      ) : (
                        <div className="w-10 h-10 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 flex items-center justify-center text-lg">
                          {post.avatar || '👤'}
                        </div>
                      )}
                      <div>
                        <div className="flex items-center space-x-2">
                          <span className="font-semibold text-gray-800 text-sm">
                            {post.user_id === currentUserId ? 'You' : post.author}
                          </span>
                          {category && (
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${category.color} text-white`}>
                              {category.icon}
                            </span>
                          )}
                        </div>
                        <span className="text-gray-500 text-xs">{getTimeAgo(post.created_at)}</span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {/* Delete button - only show for post author or admin */}
                      {(post.user_id === currentUserId || isAdmin) && (
                        <button
                          onClick={() => handleDeletePost(post.id)}
                          className="text-red-400 hover:text-red-600 transition-colors"
                          title="Delete post"
                        >
                          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Post Content */}
                  <div className="px-4 pb-3">
                    <p className="text-gray-800 text-sm leading-relaxed">{post.content}</p>
                  </div>

                  {/* Post Media (Images/Videos) */}
                  {post.image_url && (() => {
                    try {
                      // Try to parse as JSON array (new multi-media format)
                      const mediaUrls = JSON.parse(post.image_url);
                      if (Array.isArray(mediaUrls) && mediaUrls.length > 0) {
                        return (
                          <div className="px-4 pb-3">
                            <div className={`grid gap-2 ${mediaUrls.length === 1 ? 'grid-cols-1' : 'grid-cols-2'}`}>
                              {mediaUrls.map((url, index) => {
                                const isVideo = url.includes('.mp4') || url.includes('.mov') || url.includes('.webm');
                                return (
                                  <div key={index} className="relative">
                                    {isVideo ? (
                                      <video
                                        src={url}
                                        className="w-full rounded-lg"
                                        controls
                                      />
                                    ) : (
                                      <img
                                        src={url}
                                        alt={`Media ${index + 1}`}
                                        className="w-full rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                                        onClick={() => setLightboxImage(url)}
                                        onError={(e) => console.error('Media load failed:', url)}
                                      />
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      }
                    } catch (e) {
                      // Not JSON, treat as single media (old format)
                    }
                    
                    // Single image/emoji (old format)
                    return (
                      <div className="px-4 pb-3">
                        {typeof post.image_url === 'string' && (post.image_url.startsWith('data:') || post.image_url.includes('supabase.co')) ? (
                          <img
                            src={post.image_url}
                            alt="Post image"
                            className="w-full max-w-md mx-auto max-h-96 object-contain rounded-lg cursor-pointer hover:opacity-90 transition-opacity"
                            onClick={() => setLightboxImage(post.image_url)}
                            onError={(e) => console.error('Image load failed:', post.image_url)}
                          />
                        ) : (
                          <div className="bg-gray-50 rounded-lg p-8 text-center">
                            <div className="text-6xl">{post.image_url}</div>
                          </div>
                        )}
                      </div>
                    );
                  })()}

                  {/* Action Buttons */}
                  <div className="px-4 pb-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => handleLike(post.id)}
                          className="flex items-center space-x-1 hover:scale-110 transition-transform duration-200"
                        >
                          <span className={`text-xl ${likedPosts.has(post.id) ? 'text-red-500' : 'text-gray-400'}`}>
                            {likedPosts.has(post.id) ? '❤️' : '🤍'}
                          </span>
                        </button>
                        <button
                          onClick={() => toggleComments(post.id)}
                          className="text-gray-400 hover:text-gray-600 hover:scale-110 transition-transform duration-200"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleSharePost(post)}
                          className="text-gray-400 hover:text-gray-600 hover:scale-110 transition-transform duration-200"
                        >
                          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                          </svg>
                        </button>
                      </div>
                      <button 
                        onClick={() => handleSavePost(post.id)}
                        className={`hover:scale-110 transition-transform duration-200 ${savedPosts.has(post.id) ? 'text-purple-600' : 'text-gray-400 hover:text-gray-600'}`}
                      >
                        <svg className="w-6 h-6" fill={savedPosts.has(post.id) ? 'currentColor' : 'none'} stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Likes and Comments Count */}
                  <div className="px-4 pb-3">
                    <div className="text-sm">
                      <span className="font-semibold text-gray-800">{post.likes} likes</span>
                    </div>
                    {post.replies > 0 && (
                      <button 
                        onClick={() => toggleComments(post.id)}
                        className="text-gray-500 text-sm mt-1 hover:text-gray-700"
                      >
                        {expandedComments.has(post.id) ? 'Hide' : 'View all'} {post.replies} comment{post.replies !== 1 ? 's' : ''}
                      </button>
                    )}
                  </div>

                  {/* Comments Section */}
                  {expandedComments.has(post.id) && (
                    <div className="px-4 pb-3 border-t border-gray-100 pt-3">
                      {loadingComments[post.id] ? (
                        <div className="text-center py-4 text-gray-500 text-sm">Loading comments...</div>
                      ) : (
                        <div className="space-y-3 mb-3">
                          {(postComments[post.id] || []).map(comment => (
                            <div key={comment.id} className="flex items-start space-x-2">
                              {comment.avatar_url ? (
                                <img 
                                  src={comment.avatar_url} 
                                  alt={comment.user_name}
                                  className="w-7 h-7 rounded-full object-cover flex-shrink-0"
                                />
                              ) : (
                                <div className="w-7 h-7 rounded-full bg-gradient-to-r from-purple-400 to-pink-400 flex items-center justify-center text-xs flex-shrink-0">
                                  {comment.user_name?.[0]?.toUpperCase() || '👤'}
                                </div>
                              )}
                              <div className="flex-1 bg-gray-50 rounded-lg p-2">
                                <div className="flex items-center justify-between">
                                  <span className="font-semibold text-xs text-gray-800">{comment.user_name || 'Member'}</span>
                                  {(comment.user_id === currentUserId || isAdmin) && (
                                    <button
                                      onClick={() => handleDeleteComment(post.id, comment.id)}
                                      className="text-red-400 hover:text-red-600 text-xs"
                                    >
                                      Delete
                                    </button>
                                  )}
                                </div>
                                <p className="text-sm text-gray-700 mt-1">{comment.content}</p>
                                <span className="text-xs text-gray-400 mt-1">
                                  {new Date(comment.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  {/* Add Comment */}
                  <div className="px-4 pb-4 border-t border-gray-100 pt-3">
                    <div className="flex items-center space-x-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-r from-green-400 to-blue-400 flex items-center justify-center text-sm">
                        👩‍🦰
                      </div>
                      <input
                        type="text"
                        placeholder="Add a comment..."
                        value={commentInputs[post.id] || ''}
                        onChange={(e) => setCommentInputs(prev => ({ ...prev, [post.id]: e.target.value }))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            handleAddComment(post.id);
                          }
                        }}
                        className="flex-1 text-sm text-gray-600 placeholder-gray-400 border-none outline-none"
                      />
                      <button 
                        onClick={() => handleAddComment(post.id)}
                        className="text-blue-500 text-sm font-semibold hover:text-blue-600"
                      >
                        Post
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>

        {/* Load More Button */}
        {!loadingPosts && filteredPosts.length > 0 && hasMore && (
          <div className="py-8 text-center bg-white border-t border-gray-200">
            <button
              onClick={loadMorePosts}
              disabled={loadingMore}
              className="bg-gradient-to-r from-pink-500 to-purple-600 text-white px-8 py-3 rounded-full font-medium hover:shadow-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loadingMore ? (
                <span className="flex items-center space-x-2">
                  <svg className="animate-spin h-5 w-5" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Loading...</span>
                </span>
              ) : (
                'Load More Posts'
              )}
            </button>
          </div>
        )}

        {/* Community Guidelines */}
        <div className="mt-8 bg-gradient-to-r from-purple-100 to-pink-100 rounded-lg p-6 mx-4">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">Community Guidelines</h3>
          <p className="text-sm text-gray-600">
            💜 Be kind and supportive • 🌟 Share your wins and struggles • 🤝 Offer helpful advice • 
            🚫 No medical advice • ✨ Keep it positive and uplifting
          </p>
        </div>

      </div>

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
          onClick={() => setLightboxImage(null)}
        >
          <button
            onClick={() => setLightboxImage(null)}
            className="absolute top-4 right-4 text-white text-4xl hover:text-gray-300 transition-colors z-10"
          >
            ×
          </button>
          <img
            src={lightboxImage}
            alt="Full size"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
};

export default CommunityForum;
